import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const LiftTypeSelector = ({
  selectedType,
  buildingType,
  floors,
  onSelect,
  onContinue,
  onBack,
}) => {
  const [hoveredType, setHoveredType] = useState(null);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // Responsive breakpoints
  const isMobile = windowWidth <= 768;
  const isSmallScreen = windowWidth <= 480;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Define available lift types with details and compatibility
  const liftTypes = [
    {
      id: "mrl",
      name: "Machine Room-Less (MRL)",
      description:
        "Space-saving design with the machine in the shaft instead of a separate machine room.",
      image: "https://res.cloudinary.com/dknsjvavr/image/upload/v1753253376/ELevatorTypeMRL_gwxc6r.png",
      suitableFor: ["residential", "commercial", "hotel"],
      maxFloors: 10,
      capacity: "136 - 1020 kg",
      speed: "1.0 - 2.0 m/s",
      benefits: [
        "Space efficiency",
        "Energy saving technology",
        "Low maintenance",
        "Quiet operation",
      ],
      icon: "cogs",
    },
    {
      id: "hydraulic",
      name: "Hydraulic",
      description:
        "Powered by a fluid-driven piston, ideal for low to mid-rise buildings.",
      image: "https://res.cloudinary.com/dknsjvavr/image/upload/v1753254636/hydraulics_mklx7s.png",
      suitableFor: ["residential", "commercial", "hospital"],
      maxFloors: 3,
      capacity: "136 - 1020 kg",
      speed: "0.15 - 0.6 m/s",
      benefits: [
        "Lower initial cost",
        "Smooth ride quality",
        "No overhead machine room",
        "Reliable performance",
      ],
      icon: "water",
    },
    {
      id: "dumbwaiter",
      name: "Dumbwaiter",
      description:
        "Compact service elevators designed for transporting small goods between floors.",
      image: "https://res.cloudinary.com/dknsjvavr/image/upload/v1753254636/dumbwaiter_d87wtd.png",
      suitableFor: ["restaurant", "hotel", "hospital", "commercial"],
      maxFloors: 10,
      capacity: "50 - 300 kg",
      speed: "1m/s",
      benefits: [
        "Space-saving design",
        "Efficient goods transport",
        "Cost-effective installation",
        "Improved service workflow",
      ],
      icon: "utensils",
    },
    {
      id: "car-lift",
      name: "Car Lift / Freight Lift",
      description:
        "Heavy-duty elevators specifically designed for transporting vehicles between floors.",
      image: "https://res.cloudinary.com/dknsjvavr/image/upload/v1753254635/car-freight-lift_aqblgh.png",
      suitableFor: ["residential", "commercial", "parking"],
      maxFloors: 10,
      capacity: "2000 - 5000 kg",
      speed: "1.0 - 2.0 m/s",
      benefits: [
        "High load capacity",
        "Vehicle safety features",
        "Space optimization",
        "Durable construction",
      ],
      icon: "car",
    },
  ];

  // Filter types based on building type and floors
  const compatibleLiftTypes = liftTypes.filter((type) => {
    const isSuitableForBuilding =
      type.suitableFor.includes(buildingType) || buildingType === "other";
    const isWithinFloorLimit = floors <= type.maxFloors;
    return isSuitableForBuilding && isWithinFloorLimit;
  });

  // If no types are compatible, show all options
  const typesToShow =
    compatibleLiftTypes.length > 0 ? compatibleLiftTypes : liftTypes;

  // Find the selected lift type details
  const selectedLiftDetails = liftTypes.find(
    (type) => type.id === selectedType
  );

  // Helper for building type capitalization
  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Get responsive styles based on window size
  const getResponsiveStyles = () => {
    return {
      container: {
        padding: isSmallScreen
          ? "10px 12px 30px"
          : isMobile
          ? "10px 15px 35px"
          : "10px 0 40px",
      },
      introContainer: {
        padding: isSmallScreen ? "0 10px" : isMobile ? "0 15px" : "0 20px",
      },
      stepDescription: {
        fontSize: isSmallScreen ? "22px" : isMobile ? "24px" : "28px",
        marginBottom: isSmallScreen ? "12px" : "15px",
        lineHeight: isSmallScreen ? "1.2" : "1.3",
      },
      intro: {
        fontSize: isSmallScreen ? "15px" : isMobile ? "16px" : "17px",
      },
      liftTypeLayout: {
        gridTemplateColumns: isTablet || isMobile ? "1fr" : "1fr 380px",
        gap: isSmallScreen ? "25px" : isMobile ? "30px" : "40px",
      },
      cardsSection: {
        gridTemplateColumns: isSmallScreen
          ? "1fr"
          : isMobile
          ? "1fr"
          : "repeat(auto-fill, minmax(340px, 1fr))",
        gap: isSmallScreen ? "20px" : "25px",
      },
      typeCard: {
        borderRadius: isSmallScreen ? "15px" : "20px",
      },
      cardImageContainer: {
        height: isSmallScreen ? "180px" : "200px",
      },
      cardContent: {
        padding: isSmallScreen ? "20px" : "25px",
      },
      cardTitle: {
        fontSize: isSmallScreen ? "20px" : "22px",
      },
      cardDescription: {
        fontSize: isSmallScreen ? "14px" : "15px",
      },
      specs: {
        gridTemplateColumns: isSmallScreen ? "1fr" : "repeat(3, 1fr)",
        gap: isSmallScreen ? "12px" : "15px",
        padding: isSmallScreen ? "12px 0" : "15px 0",
      },
      buildingTag: {
        padding: isSmallScreen ? "3px 8px" : "4px 10px",
      },
      detailsSection: {
        padding: isSmallScreen ? "20px" : isMobile ? "25px" : "30px",
        position: isMobile ? "relative" : "sticky",
        top: isMobile ? "0" : "120px",
        borderRadius: isSmallScreen ? "15px" : "20px",
      },
      detailsTitle: {
        fontSize: isSmallScreen ? "18px" : "20px",
      },
      performanceItem: {
        flexDirection: isSmallScreen ? "column" : "row",
        alignItems: isSmallScreen ? "flex-start" : "center",
      },
      performanceIconContainer: {
        marginBottom: isSmallScreen ? "10px" : "0",
        marginRight: isSmallScreen ? "0" : "14px",
      },
      navigationContainer: {
        flexDirection: isSmallScreen ? "column" : "row",
        gap: isSmallScreen ? "15px" : "0",
        padding: isSmallScreen ? "0 10px" : "0 15px",
      },
      backButton: {
        width: isSmallScreen ? "100%" : "auto",
        justifyContent: isSmallScreen ? "center" : "flex-start",
        padding: isSmallScreen ? "12px 20px" : "14px 28px",
        fontSize: isSmallScreen ? "15px" : "16px",
      },
      continueButton: {
        width: isSmallScreen ? "100%" : "auto",
        justifyContent: isSmallScreen ? "center" : "flex-start",
        padding: isSmallScreen ? "12px 20px" : "14px 28px",
        fontSize: isSmallScreen ? "15px" : "16px",
      },
    };
  };

  const responsiveStyles = getResponsiveStyles();

  return (
    <div style={{ ...styles.container, ...responsiveStyles.container }}>
      <div
        style={{ ...styles.introContainer, ...responsiveStyles.introContainer }}
      >
        <h3
          style={{
            ...styles.stepDescription,
            ...responsiveStyles.stepDescription,
          }}
        >
          Choose the ideal elevator system for your
          <span style={styles.highlight}> {buildingType} </span>
          building with
          <span style={styles.highlight}> {floors} floors</span>
        </h3>
        <p style={{ ...styles.intro, ...responsiveStyles.intro }}>
          Each lift type offers unique advantages. Based on your building
          requirements, we've highlighted the most suitable options for you
          below.
        </p>
      </div>

      <div
        style={{ ...styles.liftTypeLayout, ...responsiveStyles.liftTypeLayout }}
      >
        {/* Lift Type Cards Section */}
        <div
          style={{ ...styles.cardsSection, ...responsiveStyles.cardsSection }}
        >
          <AnimatePresence>
            {typesToShow.map((type) => {
              const isCompatible = floors <= type.maxFloors;
              const isSelected = selectedType === type.id;

              return (
                <motion.div
                  key={type.id}
                  style={{
                    ...styles.typeCard,
                    ...responsiveStyles.typeCard,
                    ...(isSelected ? styles.selectedCard : {}),
                    ...(isCompatible ? {} : styles.incompatibleCard),
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  whileHover={
                    isCompatible
                      ? {
                          y: isSmallScreen ? 0 : -5,
                          boxShadow: "0 15px 35px rgba(22, 59, 162, 0.15)",
                        }
                      : {}
                  }
                  onClick={() => isCompatible && onSelect(type.id)}
                  onMouseEnter={() => setHoveredType(type.id)}
                  onMouseLeave={() => setHoveredType(null)}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  aria-disabled={!isCompatible}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && isCompatible) {
                      onSelect(type.id);
                      e.preventDefault();
                    }
                  }}
                >
                  <div
                    style={{
                      ...styles.cardImageContainer,
                      ...responsiveStyles.cardImageContainer,
                    }}
                  >
                    <img
                      src={type.image}
                      alt={`${type.name} elevator`}
                      style={styles.cardImage}
                      loading="lazy"
                    />
                    <div style={styles.cardImageOverlay}></div>

                    {isCompatible ? (
                      <div style={styles.cardImageIcon}>
                        <i
                          className={`fas fa-${type.icon}`}
                          style={styles.featureIcon}
                        ></i>
                      </div>
                    ) : (
                      <div style={styles.warningOverlay}>
                        <i
                          className="fas fa-exclamation-triangle"
                          style={styles.warningIcon}
                        ></i>
                        <span style={styles.warningText}>
                          Not suitable for {floors} floors
                        </span>
                      </div>
                    )}

                    {isSelected && (
                      <motion.div
                        style={styles.checkmarkOverlay}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div style={styles.checkmarkContainer}>
                          <i
                            className="fas fa-check"
                            style={styles.checkmark}
                          ></i>
                        </div>
                      </motion.div>
                    )}
                  </div>

                  <div
                    style={{
                      ...styles.cardContent,
                      ...responsiveStyles.cardContent,
                    }}
                  >
                    <div style={styles.cardHeader}>
                      <h3
                        style={{
                          ...styles.cardTitle,
                          ...responsiveStyles.cardTitle,
                        }}
                      >
                        {type.name}
                      </h3>
                      {isSelected && (
                        <div style={styles.selectedBadge}>Selected</div>
                      )}
                    </div>

                    <p
                      style={{
                        ...styles.cardDescription,
                        ...responsiveStyles.cardDescription,
                      }}
                    >
                      {type.description}
                    </p>

                    <div style={{ ...styles.specs, ...responsiveStyles.specs }}>
                      <div style={styles.specItem}>
                        <span style={styles.specLabel}>
                          <i
                            className="fas fa-weight-hanging"
                            style={styles.specIcon}
                          ></i>{" "}
                          Capacity
                        </span>
                        <span style={styles.specValue}>{type.capacity}</span>
                      </div>
                      <div style={styles.specItem}>
                        <span style={styles.specLabel}>
                          <i
                            className="fas fa-tachometer-alt"
                            style={styles.specIcon}
                          ></i>{" "}
                          Speed
                        </span>
                        <span style={styles.specValue}>{type.speed}</span>
                      </div>
                      <div style={styles.specItem}>
                        <span style={styles.specLabel}>
                          <i
                            className="fas fa-building"
                            style={styles.specIcon}
                          ></i>{" "}
                          Max Floors
                        </span>
                        <span
                          style={{
                            ...styles.specValue,
                            ...(floors > type.maxFloors
                              ? styles.exceedsMaxFloors
                              : {}),
                          }}
                        >
                          {type.maxFloors}
                          {floors > type.maxFloors && (
                            <i
                              className="fas fa-exclamation-circle"
                              style={styles.warningIconSmall}
                            ></i>
                          )}
                        </span>
                      </div>
                    </div>

                    <div style={styles.suitabilitySection}>
                      <div style={styles.suitabilityHeader}>
                        <span style={styles.suitabilityLabel}>
                          Suitable for:
                        </span>
                      </div>
                      <div style={styles.buildingTagsContainer}>
                        {type.suitableFor.map((building, index) => (
                          <span
                            key={index}
                            style={{
                              ...styles.buildingTag,
                              ...responsiveStyles.buildingTag,
                              ...(building === buildingType
                                ? styles.matchingBuildingTag
                                : {}),
                            }}
                          >
                            {building === buildingType && (
                              <i
                                className="fas fa-check"
                                style={styles.tagCheckIcon}
                              ></i>
                            )}
                            {capitalizeFirstLetter(building)}
                          </span>
                        ))}
                      </div>
                    </div>

                    <motion.div
                      style={styles.cardFooter}
                      animate={{
                        backgroundColor:
                          isSelected || hoveredType === type.id
                            ? "rgba(22, 59, 162, 0.05)"
                            : "transparent",
                      }}
                    >
                      <motion.button
                        style={{
                          ...styles.selectButton,
                          ...(isSelected ? styles.selectedButton : {}),
                          ...(isCompatible ? {} : styles.disabledButton),
                        }}
                        whileHover={
                          isCompatible && !isSelected
                            ? { scale: isSmallScreen ? 1.02 : 1.05 }
                            : {}
                        }
                        whileTap={
                          isCompatible && !isSelected ? { scale: 0.98 } : {}
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isCompatible) onSelect(type.id);
                        }}
                        disabled={!isCompatible}
                      >
                        {isSelected ? "Selected" : "Select"}
                        {isSelected && (
                          <i
                            className="fas fa-check"
                            style={styles.selectButtonIcon}
                          ></i>
                        )}
                      </motion.button>
                    </motion.div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Selected Type Details Section */}
        {selectedLiftDetails && (
          <motion.div
            style={{
              ...styles.detailsSection,
              ...responsiveStyles.detailsSection,
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div style={styles.detailsHeader}>
              <div style={styles.detailsIconContainer}>
                <i
                  className={`fas fa-${selectedLiftDetails.icon}`}
                  style={styles.detailsIcon}
                ></i>
              </div>
              <div>
                <h3
                  style={{
                    ...styles.detailsTitle,
                    ...responsiveStyles.detailsTitle,
                  }}
                >
                  {selectedLiftDetails.name}
                </h3>
                <p style={styles.detailsSubtitle}>Details & Specifications</p>
              </div>
            </div>

            <div style={styles.detailsDivider}></div>

            <div style={styles.benefitsContainer}>
              <h4 style={styles.benefitsSectionTitle}>
                <i className="fas fa-star" style={styles.sectionIcon}></i>
                Key Benefits
              </h4>

              <div style={styles.benefitsList}>
                {selectedLiftDetails.benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    style={styles.benefitItem}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + index * 0.1 }}
                  >
                    <div style={styles.benefitIconContainer}>
                      <i
                        className="fas fa-check"
                        style={styles.benefitIcon}
                      ></i>
                    </div>
                    <span style={styles.benefitText}>{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <div style={styles.detailsSuitability}>
              <h4 style={styles.detailSectionTitle}>
                <i className="fas fa-building" style={styles.sectionIcon}></i>
                Best For
              </h4>

              <div style={styles.detailsBuildingTypes}>
                {selectedLiftDetails.suitableFor.map((building, index) => (
                  <div
                    key={index}
                    style={{
                      ...styles.buildingTypeItem,
                      ...(building === buildingType
                        ? styles.currentBuildingType
                        : {}),
                    }}
                  >
                    <i
                      className={`fas fa-${getBuildingIcon(building)}`}
                      style={{
                        ...styles.buildingTypeIcon,
                        ...(building === buildingType
                          ? styles.currentBuildingTypeIcon
                          : {}),
                      }}
                    ></i>
                    <span style={styles.buildingTypeName}>
                      {capitalizeFirstLetter(building)}
                    </span>
                    {building === buildingType && (
                      <span style={styles.currentTag}>Your selection</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div style={styles.detailsPerformance}>
              <h4 style={styles.detailSectionTitle}>
                <i className="fas fa-chart-line" style={styles.sectionIcon}></i>
                Performance
              </h4>

              <div style={styles.performanceSpecs}>
                <div
                  style={{
                    ...styles.performanceItem,
                    ...responsiveStyles.performanceItem,
                  }}
                >
                  <div
                    style={{
                      ...styles.performanceIconContainer,
                      ...responsiveStyles.performanceIconContainer,
                    }}
                  >
                    <i
                      className="fas fa-weight-hanging"
                      style={styles.performanceIcon}
                    ></i>
                  </div>
                  <div style={styles.performanceDetails}>
                    <span style={styles.performanceLabel}>Load Capacity</span>
                    <span style={styles.performanceValue}>
                      {selectedLiftDetails.capacity}
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    ...styles.performanceItem,
                    ...responsiveStyles.performanceItem,
                  }}
                >
                  <div
                    style={{
                      ...styles.performanceIconContainer,
                      ...responsiveStyles.performanceIconContainer,
                    }}
                  >
                    <i
                      className="fas fa-tachometer-alt"
                      style={styles.performanceIcon}
                    ></i>
                  </div>
                  <div style={styles.performanceDetails}>
                    <span style={styles.performanceLabel}>Travel Speed</span>
                    <span style={styles.performanceValue}>
                      {selectedLiftDetails.speed}
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    ...styles.performanceItem,
                    ...responsiveStyles.performanceItem,
                  }}
                >
                  <div
                    style={{
                      ...styles.performanceIconContainer,
                      ...responsiveStyles.performanceIconContainer,
                    }}
                  >
                    <i
                      className="fas fa-ruler-vertical"
                      style={styles.performanceIcon}
                    ></i>
                  </div>
                  <div style={styles.performanceDetails}>
                    <span style={styles.performanceLabel}>Maximum Height</span>
                    <span style={styles.performanceValue}>
                      {selectedLiftDetails.maxFloors} floors
                      {floors > selectedLiftDetails.maxFloors && (
                        <span style={styles.exceedsWarning}>
                          <i className="fas fa-exclamation-circle"></i>
                          Your building exceeds this
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {floors > selectedLiftDetails.maxFloors && (
              <div style={styles.warningMessage}>
                <i
                  className="fas fa-exclamation-triangle"
                  style={styles.largeWarningIcon}
                ></i>
                <div style={styles.warningContent}>
                  <h5 style={styles.warningTitle}>Compatibility Warning</h5>
                  <p style={styles.warningText}>
                    This lift type is not recommended for {floors} floors.
                    Maximum recommended is {selectedLiftDetails.maxFloors}{" "}
                    floors. Consider alternative options or contact our
                    engineering team for a custom solution.
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Navigation buttons */}
      <motion.div
        style={{
          ...styles.navigationContainer,
          ...responsiveStyles.navigationContainer,
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <motion.button
          onClick={onBack}
          style={{ ...styles.backButton, ...responsiveStyles.backButton }}
          whileHover={{
            scale: isSmallScreen ? 1.01 : 1.03,
            backgroundColor: "#f5f5f7",
          }}
          whileTap={{ scale: 0.98 }}
          aria-label="Go back to previous step"
        >
          <i className="fas fa-chevron-left" style={styles.buttonIconLeft}></i>
          Back
        </motion.button>

        <motion.button
          style={{
            ...styles.continueButton,
            ...responsiveStyles.continueButton,
            ...(selectedType ? {} : styles.disabledContinueButton),
          }}
          whileHover={
            selectedType
              ? {
                  scale: isSmallScreen ? 1.01 : 1.03,
                  boxShadow: "0 8px 20px rgba(22, 59, 162, 0.3)",
                }
              : {}
          }
          whileTap={selectedType ? { scale: 0.98 } : {}}
          onClick={selectedType ? onContinue : undefined}
          disabled={!selectedType}
          aria-disabled={!selectedType}
          aria-label="Continue to next step"
        >
          Continue
          <i
            className="fas fa-chevron-right"
            style={styles.buttonIconRight}
          ></i>
        </motion.button>
      </motion.div>
    </div>
  );
};

// Helper function for building icons
function getBuildingIcon(buildingType) {
  const icons = {
    residential: "home",
    commercial: "building",
    hospital: "hospital",
    hotel: "hotel",
    factory: "industry",
    other: "building-shield",
  };

  return icons[buildingType] || "building";
}

const styles = {
  // Original styles remain unchanged
  container: {
    padding: "10px 0 40px",
  },
  introContainer: {
    maxWidth: "900px",
    margin: "0 auto 40px",
    textAlign: "center",
    padding: "0 20px",
  },
  stepDescription: {
    fontSize: "28px",
    fontWeight: "600",
    color: "#1d1d1f",
    margin: "0 0 15px",
    lineHeight: "1.3",
  },
  highlight: {
    color: "#163ba2",
    fontWeight: "700",
  },
  intro: {
    fontSize: "17px",
    color: "#4d4d4d",
    lineHeight: "1.6",
    margin: 0,
  },
  liftTypeLayout: {
    display: "grid",
    gridTemplateColumns: "1fr 380px",
    gap: "40px",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  cardsSection: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
    gap: "25px",
  },
  typeCard: {
    backgroundColor: "#fff",
    borderRadius: "20px",
    overflow: "hidden",
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
    cursor: "pointer",
    transition: "all 0.3s ease",
    border: "1px solid #eaeaea",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  selectedCard: {
    boxShadow: "0 0 0 2px #163ba2, 0 12px 30px rgba(22, 59, 162, 0.15)",
    borderColor: "#163ba2",
  },
  incompatibleCard: {
    opacity: 0.8,
    filter: "grayscale(30%)",
    cursor: "not-allowed",
  },
  cardImageContainer: {
    position: "relative",
    height: "200px",
    overflow: "hidden",
  },
  cardImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform 1.5s ease",
  },
  cardImageOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 30%, rgba(0,0,0,0.1) 100%)",
    zIndex: 1,
  },
  cardImageIcon: {
    position: "absolute",
    top: "15px",
    right: "15px",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
    zIndex: 2,
  },
  featureIcon: {
    color: "#163ba2",
    fontSize: "18px",
  },
  warningOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 59, 48, 0.75)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: "white",
    zIndex: 2,
  },
  warningIcon: {
    fontSize: "36px",
    marginBottom: "10px",
  },
  warningText: {
    fontSize: "16px",
    fontWeight: "600",
    textAlign: "center",
    padding: "0 20px",
  },
  checkmarkOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(22, 59, 162, 0.75)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 3,
  },
  checkmarkContainer: {
    backgroundColor: "white",
    borderRadius: "50%",
    width: "60px",
    height: "60px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 5px 15px rgba(0,0,0,0.2)",
  },
  checkmark: {
    color: "#163ba2",
    fontSize: "28px",
  },
  cardContent: {
    padding: "25px",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "12px",
  },
  cardTitle: {
    fontSize: "22px",
    fontWeight: "600",
    color: "#1d1d1f",
    margin: "0",
    flex: "1 1 auto",
  },
  selectedBadge: {
    backgroundColor: "#163ba2",
    color: "white",
    fontSize: "12px",
    fontWeight: "600",
    padding: "4px 10px",
    borderRadius: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  cardDescription: {
    fontSize: "15px",
    color: "#4d4d4d",
    lineHeight: "1.6",
    margin: "0 0 20px",
    flexGrow: 1,
  },
  specs: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "15px",
    marginBottom: "20px",
    padding: "15px 0",
    borderTop: "1px solid #eaeaea",
    borderBottom: "1px solid #eaeaea",
  },
  specItem: {
    display: "flex",
    flexDirection: "column",
  },
  specLabel: {
    fontSize: "12px",
    color: "#86868b",
    marginBottom: "5px",
    display: "flex",
    alignItems: "center",
  },
  specIcon: {
    marginRight: "5px",
    fontSize: "11px",
    color: "#163ba2",
  },
  specValue: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#1d1d1f",
  },
  exceedsMaxFloors: {
    color: "#ff3b30",
  },
  warningIconSmall: {
    marginLeft: "5px",
    fontSize: "12px",
  },
  suitabilitySection: {
    marginBottom: "20px",
  },
  suitabilityHeader: {
    marginBottom: "8px",
  },
  suitabilityLabel: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#86868b",
  },
  buildingTagsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  buildingTag: {
    backgroundColor: "#f5f5f7",
    color: "#4d4d4d",
    fontSize: "13px",
    padding: "4px 10px",
    borderRadius: "12px",
    display: "inline-flex",
    alignItems: "center",
  },
  matchingBuildingTag: {
    backgroundColor: "rgba(22, 59, 162, 0.1)",
    color: "#163ba2",
    fontWeight: "600",
  },
  tagCheckIcon: {
    marginRight: "4px",
    fontSize: "11px",
  },
  cardFooter: {
    marginTop: "auto",
    padding: "15px 0 0",
    borderTopLeftRadius: "12px",
    borderTopRightRadius: "12px",
  },
  selectButton: {
    width: "100%",
    backgroundColor: "transparent",
    border: "1px solid #163ba2",
    color: "#163ba2",
    borderRadius: "12px",
    padding: "10px 16px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
  },
  selectedButton: {
    backgroundColor: "#163ba2",
    color: "white",
  },
  disabledButton: {
    backgroundColor: "transparent",
    borderColor: "#c0c0c0",
    color: "#c0c0c0",
    cursor: "not-allowed",
  },
  selectButtonIcon: {
    marginLeft: "8px",
  },
  detailsSection: {
    backgroundColor: "#ffffff",
    borderRadius: "20px",
    padding: "30px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
    border: "1px solid #eaeaea",
    position: "sticky",
    top: "120px",
    alignSelf: "flex-start",
  },
  detailsHeader: {
    display: "flex",
    alignItems: "center",
    gap: "15px",
  },
  detailsIconContainer: {
    width: "50px",
    height: "50px",
    borderRadius: "15px",
    backgroundColor: "#163ba2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 5px 15px rgba(22, 59, 162, 0.2)",
  },
  detailsIcon: {
    color: "white",
    fontSize: "20px",
  },
  detailsTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#1d1d1f",
    margin: "0 0 5px",
  },
  detailsSubtitle: {
    fontSize: "14px",
    color: "#86868b",
    margin: 0,
  },
  detailsDivider: {
    height: "1px",
    backgroundColor: "#eaeaea",
    margin: "20px 0",
  },
  benefitsContainer: {
    marginBottom: "25px",
  },
  benefitsSectionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1d1d1f",
    margin: "0 0 15px",
    display: "flex",
    alignItems: "center",
  },
  detailSectionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1d1d1f",
    margin: "0 0 15px",
    display: "flex",
    alignItems: "center",
  },
  sectionIcon: {
    marginRight: "8px",
    color: "#163ba2",
  },
  benefitsList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  benefitItem: {
    display: "flex",
    alignItems: "center",
  },
  benefitIconContainer: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    backgroundColor: "rgba(22, 59, 162, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "12px",
    flexShrink: 0,
  },
  benefitIcon: {
    color: "#163ba2",
    fontSize: "12px",
  },
  benefitText: {
    fontSize: "15px",
    color: "#4d4d4d",
  },
  detailsSuitability: {
    margin: "25px 0",
  },
  detailsBuildingTypes: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  buildingTypeItem: {
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    backgroundColor: "#f5f5f7",
    borderRadius: "10px",
  },
  currentBuildingType: {
    backgroundColor: "rgba(22, 59, 162, 0.08)",
    boxShadow: "0 2px 8px rgba(22, 59, 162, 0.1)",
  },
  buildingTypeIcon: {
    fontSize: "16px",
    color: "#86868b",
    width: "24px",
  },
  currentBuildingTypeIcon: {
    color: "#163ba2",
  },
  buildingTypeName: {
    fontSize: "14px",
    color: "#4d4d4d",
    fontWeight: "500",
    marginLeft: "8px",
  },
  currentTag: {
    marginLeft: "auto",
    fontSize: "12px",
    fontWeight: "600",
    color: "#163ba2",
    backgroundColor: "rgba(22, 59, 162, 0.1)",
    padding: "3px 8px",
    borderRadius: "8px",
  },
  detailsPerformance: {
    marginBottom: "25px",
  },
  performanceSpecs: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  performanceItem: {
    display: "flex",
    alignItems: "center",
    padding: "10px",
    backgroundColor: "#f9f9fb",
    borderRadius: "10px",
  },
  performanceIconContainer: {
    width: "36px",
    height: "36px",
    borderRadius: "10px",
    backgroundColor: "rgba(22, 59, 162, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "14px",
  },
  performanceIcon: {
    color: "#163ba2",
    fontSize: "16px",
  },
  performanceDetails: {
    flex: 1,
  },
  performanceLabel: {
    fontSize: "12px",
    color: "#86868b",
    display: "block",
    marginBottom: "3px",
  },
  performanceValue: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1d1d1f",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
  },
  exceedsWarning: {
    fontSize: "12px",
    color: "#ff3b30",
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    fontWeight: "500",
  },
  warningMessage: {
    display: "flex",
    alignItems: "flex-start",
    gap: "15px",
    backgroundColor: "rgba(255, 59, 48, 0.08)",
    borderLeft: "4px solid #ff3b30",
    padding: "15px",
    borderRadius: "10px",
    marginTop: "20px",
  },
  largeWarningIcon: {
    color: "#ff3b30",
    fontSize: "20px",
    marginTop: "2px",
  },
  warningContent: {
    flex: 1,
  },
  warningTitle: {
    color: "#ff3b30",
    fontSize: "15px",
    fontWeight: "600",
    margin: "0 0 5px",
  },
  warningText: {
    color: "#4d4d4d",
    fontSize: "13px",
    lineHeight: "1.5",
    margin: 0,
  },
  navigationContainer: {
    display: "flex",
    justifyContent: "space-between",
    maxWidth: "1200px",
    margin: "40px auto 0",
    padding: "0 15px",
  },
  backButton: {
    backgroundColor: "transparent",
    border: "1px solid #d2d2d7",
    borderRadius: "12px",
    padding: "14px 28px",
    color: "#1d1d1f",
    fontSize: "16px",
    fontWeight: "500",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    transition: "all 0.2s ease",
    outline: "none",
  },
  buttonIconLeft: {
    marginRight: "10px",
    fontSize: "14px",
  },
  continueButton: {
    backgroundColor: "#163ba2",
    color: "white",
    border: "none",
    borderRadius: "12px",
    padding: "14px 28px",
    fontSize: "16px",
    fontWeight: "500",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 12px rgba(22, 59, 162, 0.25)",
    outline: "none",
  },
  disabledContinueButton: {
    backgroundColor: "#a0a0a0",
    cursor: "not-allowed",
    opacity: 0.7,
    boxShadow: "none",
  },
  buttonIconRight: {
    marginLeft: "10px",
    fontSize: "14px",
  },
};

export default LiftTypeSelector;
